import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { Reveal } from "@/components/silver/common/Reveal";
import { SpinStat } from "@/components/silver/common/SpinStat";
import { INSIGHT_ART } from "@/components/silver/proof/InsightArt";
import { BOTTOM_LINE, KEY_INSIGHTS } from "@/data/content";
import { cn } from "@/lib/utils";

const EASE = [0.22, 1, 0.36, 1];
const THEMES = {
  lime: { bg: "bg-[#96C11F]", title: "text-[#14213D]", body: "text-[#14213D]/80", highlight: "text-[#14213D]", stat: "text-white", statLabel: "text-white", detail: "text-[#14213D]", source: "text-[#14213D]/70", button: "border-[#14213D]/40 text-[#14213D] hover:bg-[#14213D]/10", artDark: "#14213D" },
  navy: { bg: "bg-[#14213D]", title: "text-white", body: "text-white/80", highlight: "text-[#C6EE5A]", stat: "text-[#C6EE5A]", statLabel: "text-white", detail: "text-[#C6EE5A]", source: "text-white/55", button: "border-[#C6EE5A]/50 text-[#C6EE5A] hover:bg-[#C6EE5A]/10" },
  light: { bg: "bg-[#EEF3F8]", title: "text-[#14213D]", body: "text-[#14213D]/75", highlight: "text-[#14213D]", stat: "text-[#14213D]", statLabel: "text-[#14213D]", detail: "text-[#7AA818]", source: "text-[#14213D]/55", button: "border-[#14213D]/30 text-[#14213D] hover:bg-[#14213D]/5" },
  forest: { bg: "bg-[#2E6B3A]", title: "text-white", body: "text-white/85", highlight: "text-[#C6EE5A]", stat: "text-white", statLabel: "text-white", detail: "text-[#C6EE5A]", source: "text-white/60", button: "border-white/40 text-white hover:bg-white/10" },
};

function InsightCard({ item, delay, open, onToggle }) {
  const t = THEMES[item.theme];
  const Art = INSIGHT_ART[item.art];
  return (
    <Reveal as="article" delay={delay} className={cn("relative flex min-h-[300px] flex-col overflow-hidden rounded-2xl p-5 shadow-[0_30px_70px_-30px_rgba(0,0,0,0.6)]", t.bg)} data-testid={`insight-card-${item.id}`}>
      <h3 className={cn("text-[22px] font-extrabold leading-[1.08] tracking-[-0.03em]", t.title)}>{item.kicker}</h3>

      <div className="relative mt-auto pt-6">
        <SpinStat value={item.stat} className={cn("text-[48px] font-extrabold tracking-[-0.05em]", t.stat)} data-testid={`insight-stat-${item.id}`} />
        <p className={cn("mt-2 max-w-[54%] text-[14px] font-bold leading-snug", t.statLabel)}>{item.statLabel}</p>
        {item.detail && <p className={cn("mt-2 max-w-[54%] text-[12px] font-semibold", t.detail)}>{item.detail}</p>}
        <p className={cn("mt-3 font-ui text-[9.5px] font-semibold uppercase tracking-[0.16em]", t.source)}>Source: {item.source}</p>
        <Art className="pointer-events-none absolute bottom-6 right-0 h-24 w-24" dark={t.artDark} />
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div key="more" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.5, ease: EASE }} className="overflow-hidden" data-testid={`insight-more-${item.id}`}>
            <div className="pt-6">
              <p className={cn("text-[17px] font-extrabold leading-snug tracking-[-0.02em]", t.title)}>{item.title}</p>
              <div className={cn("mt-3 space-y-3 text-[13px] leading-relaxed", t.body)}>
                {item.paragraphs.map((p, i) => (
                  <p key={i} className={cn(p.highlight && t.highlight, p.bold && cn("font-bold", t.highlight))}>{p.text}</p>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <button type="button" onClick={onToggle} aria-expanded={open} className={cn("mt-4 inline-flex w-fit items-center gap-1.5 rounded-full border px-4 py-2 font-ui text-[11px] font-bold uppercase tracking-[0.14em] transition-colors", t.button)} data-testid={`insight-toggle-${item.id}`}>
        {open ? "Show less" : "Discover more"} <ChevronDown className={cn("h-3.5 w-3.5 transition-transform duration-300", open && "rotate-180")} />
      </button>
    </Reveal>
  );
}

// Straddles the bottom edge of the hero so the photograph and animation stay clear above.
export default function KeyInsights() {
  const [open, setOpen] = useState(false);
  return (
    <div id="insights" className="relative z-10 mx-auto -mt-20 max-w-7xl px-5 pb-16 sm:px-8 lg:-mt-32 lg:pb-20" data-testid="key-insights-section">
      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
        {KEY_INSIGHTS.map((item, i) => <InsightCard key={item.id} item={item} delay={i * 0.08} open={open} onToggle={() => setOpen((v) => !v)} />)}
      </div>

      <Reveal className="glass mt-6 px-7 py-8 sm:py-10" data-testid="bottom-line-band">
        <h3 className="w-full text-2xl font-extrabold leading-tight tracking-[-0.03em] sm:text-3xl lg:text-[2.1rem]" data-testid="bottom-line-heading">{BOTTOM_LINE.question}</h3>
      </Reveal>
    </div>
  );
}
